<script setup>
import { ref, reactive, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import { useVModel } from '@vueuse/core'
import { getList } from '@/api/modules/user'
import { getUser } from '@/api/user'


const $props = defineProps({
  modelValue: {
    type: [Number, String, Array],
    default: null
  },
  multiple: {
    type: Boolean,
    default: false
  },
  maxSelect: {
    type: Number,
    default: null
  },
  excludeIds: {
    type: Array,
    default: () => []
  }
})

const $emits = defineEmits(['update:modelValue', 'confirm'])

const value = useVModel($props, 'modelValue', $emits)

const userOptions = ref([])
const userList = ref([])
const loading = ref(false)
const scrollbarRef = ref()
const hasMore = ref(true)
const pagination = reactive({
  page: 1,
  size: 10,
  total: 0
})

const toUserOption = (user) => {
  const data = user?.data || user || {}
  return {
    id: data.id,
    name: data.displayName || data.name || data.username || String(data.id),
    type: 'user',
    leaf: true,
    avatar: data.avatarUrl || data.avatar,
    username: data.username
  }
}

const isSelected = (user) => {
  return userOptions.value.some((e) => String(e.id) === String(user.id))
}

const onUserClick = (data) => {
  if ($props.multiple) {
    const index = userOptions.value.findIndex((e) => String(e.id) === String(data.id))
    if (index === -1) {
      if ($props.maxSelect && userOptions.value.length >= $props.maxSelect) {
        ElMessage.warning(`最多选择${$props.maxSelect}人`)
        return
      }
      userOptions.value.push(data)
      userOptions.value.sort((a, b) => String(a.id).localeCompare(String(b.id)))
    } else {
      userOptions.value.splice(index, 1)
    }
  } else {
    const index = userOptions.value.findIndex((e) => String(e.id) === String(data.id))
    if (index === -1) {
      userOptions.value = [data]
    } else {
      userOptions.value.splice(index, 1)
    }
  }
}
const dialogVisible = ref(false)
const queryForm = reactive({
  name: ''
})

const open = () => {
  dialogVisible.value = true
}
const fetchUsers = async ({ append = false } = {}) => {
  if (loading.value) return
  loading.value = true
  try {
    const keyword = queryForm.name?.trim()
    const res = await getList({
      page: pagination.page,
      size: pagination.size,
      username: keyword || undefined
    })
    if (res.success) {
      const records = res.data?.records || res.data || []
      const nextUsers = records.map(toUserOption)
      if (append) {
        const exists = new Set(userList.value.map(user => String(user.id)))
        userList.value = userList.value.concat(nextUsers.filter(user => !exists.has(String(user.id))))
      } else {
        userList.value = nextUsers
      }
      pagination.total = Number(res.total ?? res.data?.total ?? records.length)
      hasMore.value = pagination.total > 0
        ? userList.value.length < pagination.total
        : records.length === pagination.size
    }
  } finally {
    loading.value = false
  }
}

const loadSelectedUsers = async () => {
  // 处理已选中的用户ID，查找并填充用户信息
  let userIds = []
  if (Array.isArray(value.value)) {
    userIds.push(...value.value)
  } else if (value.value) {
    userIds.push(value.value)
  }

  if (userIds.length > 0) {
    const selected = await Promise.all(userIds.map(async id => {
      const currentPageUser = userList.value.find(u => String(u.id) === String(id))
      if (currentPageUser) return { ...currentPageUser }
      try {
        return toUserOption(await getUser(id))
      } catch (error) {
        console.error('加载用户信息失败:', error)
        return { id, name: String(id), type: 'user', leaf: true, avatar: '', username: '' }
      }
    }))
    userOptions.value = selected.filter(user => user.id)
  } else {
    userOptions.value = []
  }
}

const onOpen = async () => {
  pagination.page = 1
  hasMore.value = true
  userList.value = []
  await fetchUsers()
  await loadSelectedUsers()
}

const handleSearch = () => {
  pagination.page = 1
  hasMore.value = true
  userList.value = []
  fetchUsers()
}

const excludeIdSet = computed(() => new Set(($props.excludeIds || []).map(id => String(id))))
const visibleUserList = computed(() => userList.value.filter(user => !excludeIdSet.value.has(String(user.id))))

const loadNextPage = () => {
  if (loading.value || !hasMore.value) return
  pagination.page += 1
  fetchUsers({ append: true })
}

const handleScroll = ({ scrollTop }) => {
  const wrap = scrollbarRef.value?.wrapRef
  if (!wrap) return
  const distanceToBottom = wrap.scrollHeight - wrap.clientHeight - scrollTop
  if (distanceToBottom <= 24) {
    loadNextPage()
  }
}

const handelConfirm = () => {
  if ($props.multiple) {
    value.value = userOptions.value.map((e) => e.id)
  } else {
    if (userOptions.value.length > 0) {
      value.value = userOptions.value[0].id
    } else {
      value.value = null
    }
  }
  $emits('confirm', userOptions.value.map(u => ({ id: u.id, name: u.name, avatar: u.avatar })))
  dialogVisible.value = false
}

const clearSelected = () => {
  userOptions.value = []
}
defineExpose({
  open
})
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    @open="onOpen"
    modal-class="user-picker-overlay"
    :title="multiple ? `选择用户（已选 ${userOptions.length}${maxSelect ? '/' + maxSelect : ''}）` : '选择用户'"
    width="460px"
    class="user-picker-dialog"
    append-to-body
    destroy-on-close
  >
    <div class="user-picker-body">
      <!-- 已选区 -->
      <div v-if="multiple && userOptions.length > 0" class="user-selected-panel">
        <div class="user-selected-header">
          <span class="user-selected-title">已选 <em>{{ userOptions.length }}</em>{{ maxSelect ? ` / ${maxSelect}` : '' }}</span>
          <button v-if="userOptions.length > 0" type="button" class="user-selected-clear" @click="clearSelected">清空</button>
        </div>
        <div class="user-selected-list">
          <span v-for="user in userOptions" :key="user.id" class="user-chip" :title="user.name">
            <span class="user-chip-avatar">
              <img v-if="user.avatar" :src="user.avatar" :alt="user.name">
              <span v-else>{{ (user.name || '?').charAt(0) }}</span>
            </span>
            <span class="user-chip-name">{{ user.name }}</span>
            <button type="button" class="user-chip-close" @click.stop="onUserClick(user)" aria-label="移除">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        </div>
      </div>

      <!-- 搜索栏 -->
      <div class="user-search">
        <el-input
          v-model="queryForm.name"
          class="user-search-input"
          placeholder="搜索姓名或用户名"
          clearable
          @keyup.enter="handleSearch"
          @clear="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <el-button class="user-search-button" type="primary" @click="handleSearch">查询</el-button>
      </div>

      <!-- 用户列表 -->
      <el-scrollbar
        ref="scrollbarRef"
        v-loading="loading && userList.length === 0"
        tag="div"
        class="user-list"
        @scroll="handleScroll"
      >
        <div
          v-for="user in visibleUserList"
          :key="user.id"
          class="user-row"
          :class="{ 'is-active': isSelected(user) }"
          @click="onUserClick(user)"
        >
          <div class="user-row-main">
            <span class="user-avatar" :class="{ 'has-img': user.avatar }">
              <img v-if="user.avatar" :src="user.avatar" :alt="user.name">
              <span v-else class="user-avatar-char">{{ (user.name || '?').charAt(0) }}</span>
            </span>
            <div class="user-meta">
              <span class="user-name">{{ user.name }}</span>
              <span v-if="user.username && user.username !== user.name" class="user-username">@{{ user.username }}</span>
            </div>
          </div>
          <span class="user-check" :class="{ 'is-checked': isSelected(user) }">
            <svg v-if="isSelected(user)" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
            </svg>
          </span>
        </div>
        <div v-if="loading && userList.length > 0" class="load-hint">加载中...</div>
        <div v-else-if="!hasMore && userList.length > 0" class="load-hint">已加载全部</div>
        <el-empty v-else-if="!loading && userList.length === 0" description="暂无用户" :image-size="64" />
        <el-empty v-else-if="!loading && userList.length > 0 && visibleUserList.length === 0" description="当前列表无匹配人员" :image-size="64" />
      </el-scrollbar>
    </div>

    <template #footer>
      <div class="user-picker-footer">
        <span class="user-footer-info">
          <template v-if="multiple">已选 <em>{{ userOptions.length }}</em> 人</template>
          <template v-else-if="userOptions.length === 1">已选 {{ userOptions[0]?.name }}</template>
          <template v-else>未选择</template>
        </span>
        <div class="user-footer-actions">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handelConfirm">确认</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
:global(.user-picker-overlay) {
  position: fixed !important;
  inset: 0 !important;
}

:global(.user-picker-overlay .el-overlay-dialog) {
  position: fixed !important;
  inset: 0 !important;
  display: grid !important;
  place-items: center !important;
  padding: 16px;
  overflow: auto;
  box-sizing: border-box;
}

:global(.user-picker-dialog) {
  position: relative !important;
  top: auto !important;
  right: auto !important;
  left: auto !important;
  width: min(460px, calc(100vw - 32px)) !important;
  max-height: calc(100vh - 32px);
  margin: 0 !important;
  overflow: hidden;
  border-radius: 14px !important;
  transform: none !important;
}

:global(.user-picker-dialog .el-dialog__header) {
  padding: 16px 20px 10px !important;
  margin-right: 0 !important;
  border-bottom: 1px solid #e2e8f0;
}

:global(.user-picker-dialog .el-dialog__title) {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

:global(.user-picker-dialog .el-dialog__body) {
  padding: 14px 16px 12px !important;
}

:global(.user-picker-dialog .el-dialog__footer) {
  padding: 0 !important;
  border-top: 0 !important;
}

.user-picker-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 已选区 */
.user-selected-panel {
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background-color: #f8fbff;
  padding: 8px 10px;
}

.user-selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.user-selected-title {
  font-size: 12px;
  color: #475569;

  em {
    font-style: normal;
    font-weight: 600;
    color: #2563eb;
    margin: 0 2px;
  }
}

.user-selected-clear {
  border: 0;
  background: transparent;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: color 0.16s, background-color 0.16s;

  &:hover {
    color: #ef4444;
    background-color: #fef2f2;
  }
}

.user-selected-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 96px;
  overflow-y: auto;
}

.user-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  height: 24px;
  padding: 0 4px 0 3px;
  border-radius: 9999px;
  background-color: #ffffff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 12px;
}

.user-chip-avatar {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  overflow: hidden;
  background: linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%);
  color: #1d4ed8;
  font-size: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.user-chip-name {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.user-chip-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: currentColor;
  opacity: 0.6;
  cursor: pointer;
  transition: opacity 0.16s, background-color 0.16s;

  svg {
    width: 9px;
    height: 9px;
  }

  &:hover {
    opacity: 1;
    background-color: rgba(37, 99, 235, 0.18);
  }
}

/* 搜索栏 */
.user-search {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
}

.user-search-input {
  :deep(.el-input__wrapper) {
    min-height: 34px !important;
    border-radius: 8px !important;
    box-shadow: 0 0 0 1px #cbd5e1 inset !important;
    background-color: #ffffff !important;
    padding-left: 10px !important;
  }

  :deep(.el-input__wrapper.is-focus) {
    box-shadow: var(--form-control-focus-ring, 0 0 0 3px rgba(37, 99, 235, 0.14)), 0 0 0 1px var(--form-control-focus, #2563eb) inset !important;
  }

  :deep(.el-input__inner) {
    height: 32px !important;
    color: #0f172a;
    font-size: 13px;
    line-height: 32px !important;
  }

  :deep(.el-input__inner::placeholder) {
    color: #94a3b8;
  }

  :deep(.el-input__prefix) {
    color: #94a3b8;
    margin-right: 4px;
  }
}

.user-search-button {
  min-width: 64px;
  height: 34px;
  border-radius: 8px;
  font-weight: 500;
}

/* 用户列表 */
.user-list {
  height: 280px;
  padding: 4px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background-color: #ffffff;

  :deep(.el-scrollbar__bar.is-vertical) {
    right: 4px;
    width: 6px;
  }

  :deep(.el-scrollbar__thumb) {
    background-color: #cbd5e1;
    opacity: 0.85;
  }
}

.user-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 5px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  color: #334155;
  cursor: pointer;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease;

  &:hover {
    background-color: #f8fafc;
    border-color: #e2e8f0;
  }

  &.is-active {
    color: #1d4ed8;
    background-color: #eff6ff;
    border-color: #bfdbfe;
  }
}

.user-row-main {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
}

.user-avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  overflow: hidden;
  background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.user-avatar-char {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
}

.user-row.is-active .user-avatar:not(.has-img) {
  background: linear-gradient(135deg, #bfdbfe 0%, #60a5fa 100%);
  color: #1d4ed8;
}

.user-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}

.user-name,
.user-username {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-name {
  color: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.user-username {
  max-width: 240px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 16px;
}

.user-check {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border: 1.5px solid #cbd5e1;
  border-radius: 5px;
  background-color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  transition: all 0.16s ease;

  svg {
    width: 12px;
    height: 12px;
  }

  &.is-checked {
    border-color: #2563eb;
    background-color: #2563eb;
    color: #ffffff;
  }
}

.load-hint {
  padding: 12px 0 8px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
}

/* 底部 */
.user-picker-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.user-footer-info {
  font-size: 13px;
  color: #64748b;

  em {
    font-style: normal;
    font-weight: 600;
    color: #2563eb;
    margin: 0 2px;
  }
}

.user-footer-actions {
  display: flex;
  gap: 8px;
}

:global(.user-picker-dialog .user-footer-actions .el-button) {
  min-width: 72px;
  height: 34px;
  border-radius: 8px;
  font-weight: 500;
}

:global(.user-picker-dialog .user-footer-actions .el-button--primary) {
  background-color: #2563eb;
  border-color: #2563eb;
}
</style>
